/**
 * @flow
 */

import React, { useCallback } from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { format, isAfter, parse } from 'date-fns';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Icon } from 'semantic-ui-react';
import { isEmpty, map, orderBy, get, find } from 'lodash';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import InfiniteScroll from 'react-infinite-scroller';
import { Modal, Table, EllipsisLoader } from '../../../../components';

// A query to get appointments for specified client identified by ID
const GET_APPOINTMENTS_QUERY = gql`
    query getClientBoughtItems(
        $first: Int
        $after: String
        $client: ID!
        $salon: ID!
        $lang: String!
        $date: String!
    ) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    client(id: $client) {
                        id
                        futureAppointments(
                            salon: $salon
                            date: $date
                            limit: 10
                        ) {
                            id
                            master {
                                id
                                firstName
                                lastName
                            }
                            cabinet {
                                id
                                name(lang: $lang)
                            }
                            service {
                                id
                                name(lang: $lang)
                                price
                            }
                            invoice {
                                id
                                items {
                                    id
                                    price
                                    entity {
                                        ... on Service {
                                            id
                                            name(lang: $lang)
                                        }
                                    }
                                }
                            }
                            startAt
                        }
                        boughtItems(
                            salon: $salon
                            first: $first
                            after: $after
                        ) {
                            pageInfo {
                                endCursor
                                hasNextPage
                            }
                            edges {
                                id
                                boughtAt
                                price
                                paymentType
                                seller {
                                    ... on Master {
                                        id
                                        firstName
                                        lastName
                                    }
                                    ... on Administrator {
                                        id
                                        firstName
                                        lastName
                                    }
                                    ... on Cabinet {
                                        id
                                        name(lang: $lang)
                                    }
                                }
                                entity {
                                    ... on Service {
                                        id
                                        name(lang: $lang)
                                    }
                                    ... on Product {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

const intlMessages = defineMessages({
    CASH: {
        id: 'pages.appointments.details.invoice.InvoiceForm.cash',
        defaultMessage: 'Cash',
    },
    CC: {
        id: 'pages.appointments.details.invoice.InvoiceForm.credit_card',
        defaultMessage: 'Credit Card',
    },
    GIFT: {
        id: 'pages.appointments.details.invoice.InvoiceForm.gift_card',
        defaultMessage: 'Gift Card',
    },
    MEMBERSHIP: {
        id: 'pages.appointments.details.invoice.InvoiceForm.membership',
        defaultMessage: 'Membership',
    },
});

type AppointmentsProps = {
    client: Object,
};

type AppointmentRowProps = {
    boughtItem: Object,
};

/**
 * Show this message if list of appointments is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={4} textAlign="center">
            <FormattedMessage
                id="pages.clients.details.appointments.noAppointments"
                defaultMessage="This client don't have appointments"
            />
        </Table.Cell>
    </Table.Row>
);

const LoadMoreSpinner = () => <EllipsisLoader size="1em" />;

/**
 * A row of single appointment
 */
const AppointmentRow = ({ boughtItem, intl }: AppointmentRowProps) => {
    const styledRow = isAfter(
        parse(boughtItem.boughtAt, 'dd.MM.yyyy HH:mm', new Date()),
        new Date(),
    );
    const TableRow = styled(Table.Row)`
        ${styledRow && ' font-weight: bold;'}
    `;

    const iconNames = {
        CASH: 'money',
        CC: 'credit card',
        GIFT: 'gift',
        MEMBERSHIP: 'address card outline',
    };

    return (
        <TableRow>
            <Table.Cell>
                {/* we need to parse number from string, because we receive timestamp from the server as a string
            this is a temporary solution until the problem with the data types in graphql is resolved */}
                {boughtItem.boughtAt}
            </Table.Cell>
            <Table.Cell>
                {`${boughtItem.firstName} ${boughtItem.lastName || ''}`}
            </Table.Cell>
            <Table.Cell>{boughtItem.name}</Table.Cell>
            <Table.Cell textAlign="right">
                {`${boughtItem.price.toFixed(2)} €`}
            </Table.Cell>
            <Table.Cell
                textAlign="center"
                title={
                    boughtItem.paymentType
                        ? intl.formatMessage(
                              intlMessages[boughtItem.paymentType],
                          )
                        : ''
                }
                style={{ width: '50px' }}
            >
                {boughtItem.paymentType && (
                    <Icon
                        name={iconNames[boughtItem.paymentType]}
                        size="large"
                    />
                )}
            </Table.Cell>
        </TableRow>
    );
};

const AppointmentsView = ({
    boughtItems,
    futureAppointments,
    loadMore,
    pageInfo,
    intl,
}) => {
    const sortedItems =
        futureAppointments &&
        boughtItems &&
        orderBy(
            [...futureAppointments, ...boughtItems],
            i => parse(i.boughtAt, 'dd.MM.yyyy HH:mm', new Date()),
            ['desc'],
        );

    return (
        <Modal.Context>
            <Modal.Content>
                <div
                    style={{
                        maxHeight: '400px',
                        overflow: 'auto',
                        width: '100%',
                    }}
                >
                    <InfiniteScroll
                        pageStart={0}
                        initialLoad={false}
                        loadMore={loadMore}
                        hasMore={pageInfo.hasNextPage}
                        loader={
                            boughtItems.length <= 10 ? (
                                ''
                            ) : (
                                <LoadMoreSpinner key={0} />
                            )
                        }
                        useWindow={false}
                    >
                        <Table celled striped selectable>
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell>
                                        <FormattedMessage
                                            id="pages.clients.details.appointments.date"
                                            defaultMessage="Date"
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <FormattedMessage
                                            id="pages.clients.details.appointments.master"
                                            defaultMessage="Master"
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <FormattedMessage
                                            id="pages.clients.details.appointments.service"
                                            defaultMessage="Service"
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell textAlign="right">
                                        <FormattedMessage
                                            id="pages.clients.details.appointments.price"
                                            defaultMessage="Price"
                                        />
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        style={{ width: '50px' }}
                                    />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {isEmpty(futureAppointments) &&
                                isEmpty(boughtItems) ? (
                                    <EmptyMessage />
                                ) : (
                                    map(sortedItems, (item, key) => (
                                        <AppointmentRow
                                            key={key}
                                            boughtItem={item}
                                            intl={intl}
                                        />
                                    ))
                                )}
                            </Table.Body>
                        </Table>
                    </InfiniteScroll>
                </div>
            </Modal.Content>
        </Modal.Context>
    );
};

const AppointmentsViewWithIntl = injectIntl(AppointmentsView);

/**
 * Self-contained Appointments: fetches its own data via useQuery
 */
const Appointments = ({ client }: AppointmentsProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { data, loading, fetchMore } = useQuery(GET_APPOINTMENTS_QUERY, {
        variables: {
            first: 20,
            salon,
            client: client.id,
            lang: currentLanguage,
            date: new Date().toISOString().substring(0, 16) + ':00.000Z',
        },
        fetchPolicy: 'cache-and-network',
    });

    const pageInfo = get(data, 'viewer.salon.client.boughtItems.pageInfo', {});

    const boughtItems = map(
        get(data, 'viewer.salon.client.boughtItems.edges'),
        item => ({
            id: item.id,
            boughtAt: format(new Date(item.boughtAt), 'dd.MM.yyyy HH:mm'),
            price: item.price,
            name: item.entity.name,
            firstName: item.seller.firstName || item.seller.name,
            lastName: item.seller.lastName || '',
            paymentType: item.paymentType,
        }),
    );

    const futureAppointments = map(
        get(data, 'viewer.salon.client.futureAppointments'),
        item => {
            let price = parseFloat(item.service.price);

            // Set service price from invoice if future appointment has it
            if (
                item.invoice &&
                item.invoice.items &&
                item.invoice.items.length
            ) {
                const invoiceItem = find(
                    get(item.invoice, 'items'),
                    i => i.entity.id === item.service.id,
                );

                if (invoiceItem) {
                    price = parseFloat(invoiceItem.price);
                }
            }
            return {
                id: item.id,
                boughtAt: format(new Date(item.startAt), 'dd.MM.yyyy HH:mm'),
                price: price,
                name: item.service.name,
                firstName: item.master
                    ? item.master.firstName
                    : item.cabinet.name,
                lastName: item.master ? item.master.lastName : '',
            };
        },
    );

    const loadMore = useCallback(() => {
        // Skip if we already loading results or that was the last page
        if (loading || !pageInfo.hasNextPage) {
            return false;
        }

        fetchMore({
            query: GET_APPOINTMENTS_QUERY,
            variables: {
                first: 20,
                after: pageInfo.endCursor,
                salon,
                client: client.id,
                lang: currentLanguage,
                date: new Date().toISOString().substring(0, 16) + ':00.000Z',
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const prevEdges =
                    previousResult.viewer.salon.client.boughtItems.edges;
                const newEdges =
                    fetchMoreResult.viewer.salon.client.boughtItems.edges;

                const newResults = Object.assign({}, fetchMoreResult);
                newResults.viewer.salon.client.boughtItems.edges = [
                    ...prevEdges,
                    ...newEdges,
                ];

                return newResults;
            },
        });
    }, [loading, pageInfo, salon, client, currentLanguage, fetchMore]);

    return (
        <AppointmentsViewWithIntl
            boughtItems={boughtItems || []}
            futureAppointments={futureAppointments || []}
            loadMore={loadMore}
            pageInfo={pageInfo}
        />
    );
};

export default Appointments;
